import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ClipLoader } from 'react-spinners';
import { Select } from 'antd';
import {
  BookOpen,
  Circle,
  Droplets,
  LayoutGrid,
  Pencil,
  Scissors,
  Shapes,
  Smile,
  Sparkles,
  Star,
  Tv,
  Zap,
  type LucideIcon,
} from 'lucide-react';
import { toast } from 'react-toastify';
import {
  GenerateCharacterVideoResponse,
  handleCharacterVideoGeneration,
  handleLiveActionVideoAnimation,
  handleVideoDraftEnhancement,
  generateVideoScript,
  InsufficientPixlesError,
  NoActiveEntitlementError,
  ScriptCharacterReference,
  VideoResolution,
  VideoScriptScene,
  VIDEO_PIXELS_PER_SECOND,
} from '../../services/aiVideoService';
import { getPixelsSummary } from '../../services/pixelsService';
import {
  getUploadedAssets,
  saveUploadedAsset,
  UploadedAsset,
} from '../../services/uploadedAssetService';
import {
  getMediaFileType,
  uploadAssetToVideo,
} from '../../services/uploadedAssetStorageService';
import { getAssetDownloadUrl } from '../../services/assetUrlUtils';
import {
  composeAnimationPrompt,
  ANIMATION_STYLE_PRESETS,
  AnimationStylePreset,
  MAX_LIVE_ACTION_PROMPT_CHARS,
} from '../../services/animationStylePresets';
import {
  estimateLiveActionPixels,
  LIVE_ACTION_RATES,
} from '../../services/liveActionPricing';
import { getAllRiggedCharacters } from '../../services/riggedCharacterService';
import {
  getAllEnvironments,
  hydrateEnvironmentsForCurrentUser,
} from '../../services/environmentService';
import { Environment, EnvironmentSnapshot } from '../../types/EnvironmentTypes';
import {
  resolveCharacterImageUrl,
  resolveCharacterVoiceUrl,
  useCharacterImageUrl,
} from '../../services/characterStorageService';
import { isRunwayGen4TurboEnabled } from '../../services/featureFlagsService';
import { trackClarityEvent } from '../../services/clarityService';
import {
  formatLiveActionNumber,
  liveActionMessage,
} from '../../i18n/liveActionMessages';
import { toastMessage } from '../../i18n/toastMessages';
import {
  getImageAspectRatioBounds,
  isImageAspectRatioWithinBounds,
} from '../../services/videoImageLimits';
import { CharacterRig } from '../../types/RiggingTypes';
import PixlesCoin from '../billing/PixlesCoin';
import GeneratedVideoPlayer from './CharacterVideoPlayer';
import {
  isFullTrimRange,
  isTrimRangeValid as isTrimRangeValidFn,
} from '../../services/videoTrim';
import './CharacterLibrary.css';
import '../environmentlibrary/EnvironmentLibrary.css';

interface CharacterVideoGeneratorProps {
  mode?: 'character' | 'liveAction';
  videoId?: string;
  videoSasToken?: string;
  onCancel: () => void;
  onVideoGenerated: (
    videoUrl: string,
    prompt: string,
    options?: {
      duration?: number;
      provider?: string;
      trim?: { startTime: number; endTime: number };
      mute?: boolean;
    }
  ) => Promise<void> | void;
  /**
   * Opens the Character Studio so the user can modify the character being used
   * in this video workflow. Receives the currently selected library character
   * id (if any) so the studio can focus directly on it.
   */
  onOpenCharacterStudio?: (characterId?: string) => void;
  /**
   * Bumped by the parent whenever the Character Studio closes, so this
   * component can refresh its cached library cards with any edits made there
   * while the video generator stays open underneath.
   */
  libraryRevision?: number;
  /**
   * Bumped by the parent whenever the Environment Library closes, so the
   * environment picker refreshes with creations/edits/deletions made while
   * the video generator stays open underneath.
   */
  environmentRevision?: number;
  /**
   * Opens the Environment Library (create/edit environments) without
   * leaving the video workflow.
   */
  onOpenEnvironmentLibrary?: () => void;
  /**
   * Notifies the parent whenever there is an unsaved generated video, so
   * overlay/header close buttons can confirm before discarding it.
   */
  onUnsavedChange?: (hasUnsaved: boolean) => void;
}

interface CharacterVideoLibraryCardProps {
  character: CharacterRig;
  isSelected: boolean;
  disabled: boolean;
  onSelect: () => void;
}

/**
 * A single character library card in the character-to-video modal. Resolves
 * the stored (relative blob path) imageUrl with the user SAS token so the
 * thumbnail renders from the user's private storage.
 */
const CharacterVideoLibraryCard: React.FC<CharacterVideoLibraryCardProps> = ({
  character,
  isSelected,
  disabled,
  onSelect,
}) => {
  const characterName =
    character.name || character.identity?.name || character.imageName;
  const imageSrc = useCharacterImageUrl(character.imageUrl);

  return (
    <button
      type="button"
      className={`character-video-library-card ${isSelected ? 'selected' : ''}`}
      onClick={onSelect}
      disabled={disabled}
      title={`Use ${characterName}`}
    >
      <div className="character-video-library-thumbnail">
        <img src={imageSrc} alt={characterName} />
      </div>
      <div className="character-video-library-card-copy">
        <strong>{characterName}</strong>
        <span>
          {character.assets?.length ?? 1} asset
          {(character.assets?.length ?? 1) === 1 ? '' : 's'}
        </span>
        {(character.identity?.gender ||
          character.identity?.species ||
          (character.identity?.personality ?? []).length > 0) && (
          <div className="character-video-library-identity">
            {character.identity?.gender && (
              <span className="character-video-library-age">
                Gender: {character.identity.gender}
              </span>
            )}
            {character.identity?.species && (
              <span className="character-video-library-age">
                Species: {character.identity.species}
              </span>
            )}
            {(character.identity?.personality ?? []).length > 0 && (
              <div className="character-video-library-personality">
                {(character.identity?.personality ?? []).map((trait) => (
                  <span key={trait} className="personality-chip">
                    {trait}
                  </span>
                ))}
              </div>
            )}
          </div>
        )}
      </div>
    </button>
  );
};

/** Maximum library characters selectable for one video workflow. */
const MAX_SELECTED_CHARACTERS = 3;

interface EnvironmentVideoOptionCardProps {
  environment: Environment;
  isSelected: boolean;
  disabled: boolean;
  onSelect: () => void;
}

/**
 * A single environment option in the character-to-video modal. The first
 * reference image is used as the thumbnail.
 */
const EnvironmentVideoOptionCard: React.FC<EnvironmentVideoOptionCardProps> = ({
  environment,
  isSelected,
  disabled,
  onSelect,
}) => {
  const primaryImage = environment.referenceImages[0];
  const imageSrc = useCharacterImageUrl(primaryImage?.url);
  const referenceCount = environment.referenceImages.length;

  return (
    <button
      type="button"
      className={`character-video-environment-option ${isSelected ? 'selected' : ''}`}
      onClick={onSelect}
      disabled={disabled}
      title={`Set the scene in ${environment.name}`}
    >
      {imageSrc ? (
        <img src={imageSrc} alt={environment.name} />
      ) : (
        <div className="character-video-environment-empty">No preview</div>
      )}
      <div className="character-video-environment-option-copy">
        <strong>{environment.name}</strong>
        <span>
          {referenceCount} reference image{referenceCount === 1 ? '' : 's'}
        </span>
      </div>
    </button>
  );
};

/**
 * Single environment reference thumbnail. Split out so each image gets its
 * own `useCharacterImageUrl` hook (hooks can't be called in a loop).
 */
const EnvReferenceThumb: React.FC<{ url: string; alt: string }> = ({
  url,
  alt,
}) => {
  const src = useCharacterImageUrl(url);
  if (!src) return null;
  return <img src={src} alt={alt} />;
};

/**
 * Slim location bar rendered below the character preview image (not over
 * it). Shows the first reference view + env name only to avoid clutter.
 * Purely visual, generation payload is unchanged.
 */
const CharacterVideoPreviewEnvPip: React.FC<{ environment: Environment }> = ({
  environment,
}) => {
  const primary = environment.referenceImages[0];
  if (!primary) return null;
  return (
    <div
      className="character-video-preview-env-pip"
      title={`Location: ${environment.name}`}
    >
      <EnvReferenceThumb
        url={primary.url}
        alt={primary.name || environment.name}
      />
      <span className="character-video-preview-env-caption">
        📍 {environment.name}
      </span>
    </div>
  );
};

const LIVE_ACTION_MAX_SECONDS = 30;
const LIVE_ACTION_MIN_SECONDS = 3;
const MAX_LIVE_ACTION_SOURCE_BYTES = 32_000_000;

const STYLE_ICON_BY_ID: Record<string, LucideIcon> = {
  anime: Sparkles,
  'hand-drawn': Pencil,
  watercolor: Droplets,
  comic: Zap,
  storybook: BookOpen,
  'flat-vector': Shapes,
  'classic-2d': Star,
  'modern-flat-2d': LayoutGrid,
  'rubber-hose': Smile,
  cutout: Scissors,
  'retro-2d': Tv,
};

const styleOptionLabel = (label: string, Icon: LucideIcon) => (
  <span className="character-video-style-option-label">
    <span className="character-video-style-option-icon" aria-hidden="true">
      <Icon size={14} strokeWidth={2.1} />
    </span>
    <span>{label}</span>
  </span>
);

const CHARACTER_STYLE_OPTIONS = [
  { value: '', label: styleOptionLabel('Default', Circle) },
  ...ANIMATION_STYLE_PRESETS.map((preset) => ({
    value: preset.id,
    label: styleOptionLabel(
      preset.label,
      STYLE_ICON_BY_ID[preset.id] ?? Circle
    ),
  })),
];

const readImageFile = (
  file: File
): Promise<{ width: number; height: number; dataUrl: string }> =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = (event) => {
      const dataUrl = String(event.target?.result ?? '');
      const image = new Image();
      image.onload = () =>
        resolve({
          width: image.naturalWidth || image.width,
          height: image.naturalHeight || image.height,
          dataUrl,
        });
      image.onerror = () => reject(new Error('Failed to read image dimensions'));
      image.src = dataUrl;
    };
    reader.onerror = () => reject(new Error('Failed to read image file'));
    reader.readAsDataURL(file);
  });

const CharacterVideoGenerator: React.FC<CharacterVideoGeneratorProps> = ({
  mode = 'character',
  videoId,
  videoSasToken,
  onCancel,
  onVideoGenerated,
  onOpenCharacterStudio,
  libraryRevision,
  environmentRevision,
  onOpenEnvironmentLibrary,
  onUnsavedChange,
}) => {
  const isLiveAction = mode === 'liveAction';
  const maxSelectedCharacters = isLiveAction ? 1 : MAX_SELECTED_CHARACTERS;
  const imageAspectModel = isLiveAction
    ? 'kling_4'
    : isRunwayGen4TurboEnabled()
      ? 'gen4_turbo'
      : 'seedance2_5';
  const imageAspectBounds = getImageAspectRatioBounds(imageAspectModel);
  const [prompt, setPrompt] = useState('');
  const [liveActionStyleId, setLiveActionStyleId] = useState<string | null>(
    null
  );
  const [characterStyleId, setCharacterStyleId] = useState<string | null>(null);
  // Live-action source: either a local file (uploaded on generate) or a
  // previously uploaded asset under this video. Exactly one is set.
  const [liveActionFile, setLiveActionFile] = useState<File | null>(null);
  const [liveActionLocation, setLiveActionLocation] = useState('');
  const [liveActionPreviewUrl, setLiveActionPreviewUrl] = useState('');
  const [liveActionDuration, setLiveActionDuration] = useState<number | null>(
    null
  );
  const [liveActionAssets, setLiveActionAssets] = useState<UploadedAsset[]>([]);
  const [isLoadingLiveActionAssets, setIsLoadingLiveActionAssets] =
    useState(false);
  const [isUploadingSource, setIsUploadingSource] = useState(false);
  const [liveActionError, setLiveActionError] = useState('');
  const [isGenerating, setIsGenerating] = useState(false);
  // Completion percentage streamed by the backend (~every 10s over SSE).
  // Null when idle; reset on each generation attempt. The source tells
  // whether the number is the provider's own progress or a time-based
  // estimate (Seedance only returns a status string).
  const [videoProgress, setVideoProgress] = useState<number | null>(null);
  const [videoProgressSource, setVideoProgressSource] = useState<
    'provider' | 'estimated' | null
  >(null);
  const [isGeneratingScript, setIsGeneratingScript] = useState(false);
  // Scene-structured script from the planner: one prompt + duration estimate
  // per scene. The video stage currently takes a single prompt, so scenes
  // are joined when generating (single-scene output joins to itself).
  const [generatedScenes, setGeneratedScenes] = useState<VideoScriptScene[]>(
    []
  );
  // User override for the Scene 1 clip length (seconds). Null = use the
  // planner's estimate. Cleared whenever the script is (re)generated or the
  // character source changes, so a stale value never applies to new scenes.
  const [sceneDurationOverride, setSceneDurationOverride] = useState<
    number | null
  >(null);
  // Clip-duration bounds from the backend for the resolved model (null until
  // the first script response; 4–30 fallback matches the documented range).
  const [clipDurationBounds, setClipDurationBounds] = useState<{
    min: number;
    max: number;
  } | null>(null);
  const durationBounds = clipDurationBounds ?? { min: 4, max: 30 };
  const [generatedVideoUrl, setGeneratedVideoUrl] = useState('');
  const [generatedVideoPrompt, setGeneratedVideoPrompt] = useState('');
  const [generatedVideoDuration, setGeneratedVideoDuration] = useState<
    number | undefined
  >(undefined);
  const [videoTrimStart, setVideoTrimStart] = useState(0);
  const [videoTrimEnd, setVideoTrimEnd] = useState(0);
  const [libraryCharacters, setLibraryCharacters] = useState<CharacterRig[]>(
    []
  );
  // Reusable location for the scene (single-select, optional). Sent by
  // value like characters[]: name/description plus resolved (signed)
  // reference image URLs, so generation uses exactly what was selected.
  const [environments, setEnvironments] = useState<Environment[]>([]);
  const [selectedEnvironmentId, setSelectedEnvironmentId] = useState<
    string | null
  >(null);
  // Multi-select (up to MAX_SELECTED_CHARACTERS) library character ids.
  const [selectedLibraryCharacterIds, setSelectedLibraryCharacterIds] =
    useState<string[]>([]);
  // Custom uploaded image (dataURL). Uploads clear the library selection and
  // vice versa; doubles as the video-call primary image (may be empty when
  // the user works library-only — the backend then picks images from
  // `characters[].image`).
  const [customImagePreview, setCustomImagePreview] = useState<string>('');
  // Resolved (signed) image URL per selected library character id.
  const [characterImageMap, setCharacterImageMap] = useState<
    Record<string, string>
  >({});
  // Resolved (signed) voice reference per selected library character id.
  const [selectedVoiceReferences, setSelectedVoiceReferences] = useState<
    Record<string, { url: string; label: string }>
  >({});
  // Mirror of the library selection. Kept in a ref so the studio-close
  // refresh effect can read the latest values without re-running on every
  // state change.
  const selectedLibraryCharacterIdsRef = useRef<string[]>([]);
  // Active slide of the selected-characters preview carousel.
  const [previewIndex, setPreviewIndex] = useState(0);
  // Known pixel-credit balance from GET /pixels/summary (null = unknown yet
  // or backend omitted it; existing behavior applies). Refreshed from the
  // `complete` event's `available` field after each generation.
  const [pixelsBalance, setPixelsBalance] = useState<number | null>(null);
  // Trial metadata for the credits pill; `trialActive === false` marks a
  // pre-existing user with no trial, so no trial text is shown.
  const [trialActive, setTrialActive] = useState<boolean | null>(null);
  const [trialDaysRemaining, setTrialDaysRemaining] = useState<number | null>(
    null
  );
  // Set when the backend rejects a generation with 402 (or the known balance
  // is below the pre-flight estimate renders the gate message separately).
  const [insufficientCredits, setInsufficientCredits] = useState<{
    required: number;
    available: number;
  } | null>(null);
  // Set when the backend rejects a generation with 403 NO_ACTIVE_ENTITLEMENT.
  // Renders a subscribe upsell (distinct from the 402 top-up upsell).
  const [noActiveEntitlement, setNoActiveEntitlement] = useState(false);
  // Native AI-generated soundtrack toggle (default ON). Named to avoid
  // colliding with the `generateAudio` text-to-speech import used elsewhere.
  // Session-only state: resets on every fresh mount.
  const [includeAudio, setIncludeAudio] = useState(true);
  // Live action uses Kling 4.0, which always generates sound. `preserve`
  // discards it and remuxes the source clip's audio instead; `none` keeps the
  // generated audio during preview but strips it when saving to the timeline.
  const [liveActionAudioMode, setLiveActionAudioMode] = useState<
    'generate' | 'preserve' | 'none'
  >('generate');
  // Output resolution tier (480p = cheap enhanceable draft preview). The
  // choices, default, and per-second rates come from the script response;
  // these defaults only apply before a script exists.
  const [resolution, setResolution] = useState<VideoResolution>('720p');
  const [resolutionOptions, setResolutionOptions] = useState<VideoResolution[]>(
    ['720p']
  );
  const [pixelsPerSecondMap, setPixelsPerSecondMap] = useState<
    Record<string, number>
  >(VIDEO_PIXELS_PER_SECOND);
  // Handle of a completed 480p draft (null otherwise / once enhanced).
  const [draftTaskId, setDraftTaskId] = useState<string | null>(null);
  // Resolved clip length (seconds) of the draft, for the enhance cost hint.
  const [draftDurationSeconds, setDraftDurationSeconds] = useState<
    number | null
  >(null);
  const [isEnhancing, setIsEnhancing] = useState(false);
  // True while the parent persists the video to storage and patches the
  // timeline document (a slow download + upload + PATCH). Blocks the modal and
  // shows a spinner on the save button until it settles.
  const [isSavingToTimeline, setIsSavingToTimeline] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    if (!isLiveAction || !videoId) return;
    const token = localStorage.getItem('authToken');
    if (!token) return;
    let cancelled = false;
    setIsLoadingLiveActionAssets(true);
    getUploadedAssets(token, videoId)
      .then((assets) => {
        if (cancelled) return;
        setLiveActionAssets(
          assets.filter(
            (asset) =>
              asset.type === 'video' &&
              asset.location.startsWith(`videos/${videoId}/assets/`)
          )
        );
      })
      .catch((error) => {
        console.error('Failed to load video assets:', error);
      })
      .finally(() => {
        if (!cancelled) setIsLoadingLiveActionAssets(false);
      });
    return () => {
      cancelled = true;
    };
  }, [isLiveAction, videoId]);

  useEffect(
    () => () => {
      if (liveActionPreviewUrl.startsWith('blob:')) {
        URL.revokeObjectURL(liveActionPreviewUrl);
      }
    },
    [liveActionPreviewUrl]
  );

  // Live action skips the script planner, which normally publishes the model's
  // resolution tiers, so seed Kling 4.0's capabilities directly.
  useEffect(() => {
    if (!isLiveAction) return;
    setResolutionOptions(['720p', '1080p']);
    setResolution((prev) => (prev === '1080p' ? '1080p' : '720p'));
  }, [isLiveAction]);

  // Load the credit balance once for the pill + pre-flight gating. Failures
  // are silent: an unknown balance simply disables the gating.
  useEffect(() => {
    const token = localStorage.getItem('authToken');
    if (!token) return;
    let cancelled = false;
    void getPixelsSummary(token)
      .then((summary) => {
        if (cancelled) return;
        setPixelsBalance(summary.available);
        setTrialActive(summary.trial?.active ?? null);
        setTrialDaysRemaining(summary.trial?.daysRemaining ?? null);
      })
      .catch(() => undefined);
    return () => {
      cancelled = true;
    };
  }, []);

  // Load the library when the modal opens and again whenever the parent bumps
  // `libraryRevision` (i.e. the Character Studio closed after edits). While the
  // studio was open underlying characters may have changed or been deleted,
  // so re-sync the list and refresh per-character images/voices accordingly.
  useEffect(() => {
    const loaded = getAllRiggedCharacters();
    setLibraryCharacters(loaded);

    const selectedIds = selectedLibraryCharacterIdsRef.current;
    if (selectedIds.length === 0) return;

    // Drop selections deleted in the studio.
    const survivingIds = selectedIds.filter((id) =>
      loaded.some((character) => character.id === id)
    );
    if (survivingIds.length !== selectedIds.length) {
      selectedLibraryCharacterIdsRef.current = survivingIds;
      setSelectedLibraryCharacterIds(survivingIds);
      setCharacterImageMap((prev) =>
        Object.fromEntries(
          Object.entries(prev).filter(([id]) => survivingIds.includes(id))
        )
      );
      setSelectedVoiceReferences((prev) =>
        Object.fromEntries(
          Object.entries(prev).filter(([id]) => survivingIds.includes(id))
        )
      );
    }

    // Re-resolve images and voices of surviving selections so the preview
    // reflects studio edits.
    for (const id of survivingIds) {
      const refreshedCharacter = loaded.find(
        (character) => character.id === id
      );
      if (!refreshedCharacter) continue;

      void resolveCharacterImageUrl(refreshedCharacter.imageUrl)
        .then((resolved) => {
          if (!resolved) return;
          setCharacterImageMap((prev) =>
            prev[id] === resolved ? prev : { ...prev, [id]: resolved }
          );
        })
        .catch(() => undefined);

      if (refreshedCharacter.voiceUrl) {
        void resolveCharacterVoiceUrl(refreshedCharacter.voiceUrl)
          .then((resolved) => {
            if (!resolved) return;
            const voiceAsset = refreshedCharacter.assets?.find(
              (asset) => asset.type === 'audio_reference'
            );
            const next = {
              url: resolved,
              label: voiceAsset?.name || 'voice reference',
            };
            setSelectedVoiceReferences((prev) =>
              prev[id]?.url === next.url && prev[id]?.label === next.label
                ? prev
                : { ...prev, [id]: next }
            );
          })
          .catch(() => undefined);
      } else {
        setSelectedVoiceReferences((prev) => {
          if (!(id in prev)) return prev;
          const next = { ...prev };
          delete next[id];
          return next;
        });
      }
    }
  }, [libraryRevision]);

  // Load environments for the picker when the modal opens and again
  // whenever the parent bumps `environmentRevision` (i.e. the Environment
  // Library closed after edits). Drops the selection if it was deleted.
  useEffect(() => {
    const cached = getAllEnvironments();
    setEnvironments(cached);
    setSelectedEnvironmentId((prev) =>
      prev && !cached.some((env) => env.id === prev) ? null : prev
    );
    void hydrateEnvironmentsForCurrentUser()
      .then((hydrated) => {
        setEnvironments(hydrated);
        setSelectedEnvironmentId((prev) =>
          prev && !hydrated.some((env) => env.id === prev) ? null : prev
        );
      })
      .catch(() => undefined);
  }, [environmentRevision]);

  const clearGeneratedOutputs = () => {
    setGeneratedVideoUrl('');
    setGeneratedVideoPrompt('');
    setGeneratedVideoDuration(undefined);
    setGeneratedScenes([]);
    setSceneDurationOverride(null);
    setClipDurationBounds(null);
  };

  const confirmDiscardGeneratedVideo = (): boolean =>
    !generatedVideoUrl ||
    window.confirm(toastMessage('characterVideo.discardVideoConfirm'));

  const isImageAspectRatioAllowed = (
    width: number,
    height: number
  ): boolean => {
    if (!imageAspectBounds || width <= 0 || height <= 0) return true;
    if (isImageAspectRatioWithinBounds(width, height, imageAspectBounds)) {
      return true;
    }
    const ratio = width / height;
    toast.error(
      liveActionMessage('imageAspectRatio', {
        ratio: formatLiveActionNumber(ratio, 3),
        min: formatLiveActionNumber(imageAspectBounds.min, 3),
        max: formatLiveActionNumber(imageAspectBounds.max, 3),
      })
    );
    return false;
  };

  const resetLiveActionSource = (): boolean => {
    if (!confirmDiscardGeneratedVideo()) return false;
    setLiveActionFile(null);
    setLiveActionLocation('');
    setLiveActionPreviewUrl('');
    setLiveActionDuration(null);
    setLiveActionError('');
    clearGeneratedOutputs();
    return true;
  };

  const handleStylePresetToggle = (preset: AnimationStylePreset) => {
    const nextId = liveActionStyleId === preset.id ? null : preset.id;
    const nextLimit =
      MAX_LIVE_ACTION_PROMPT_CHARS - (nextId ? preset.fragment.length + 2 : 0);
    if (prompt.length > nextLimit) {
      toast.error(
        liveActionMessage('styleDirectionTooLong', {
          limit: formatLiveActionNumber(nextLimit),
        })
      );
      return;
    }
    if (!confirmDiscardGeneratedVideo()) return;
    setLiveActionStyleId(nextId);
    clearGeneratedOutputs();
  };

  const handleCharacterStyleChange = (value: string) => {
    const nextId = value || null;
    if (nextId === characterStyleId) return;
    if (!confirmDiscardGeneratedVideo()) return;
    setCharacterStyleId(nextId);
    clearGeneratedOutputs();
  };

  const handleLiveActionFileSelect = (file: File | undefined) => {
    if (!file) return;
    if (getMediaFileType(file) !== 'video') {
      toast.error(liveActionMessage('selectVideoFile'));
      return;
    }
    if (file.size > MAX_LIVE_ACTION_SOURCE_BYTES) {
      trackClarityEvent('live_action_source_too_large');
      toast.error(
        liveActionMessage('sourceTooLarge', {
          maxMb: formatLiveActionNumber(
            MAX_LIVE_ACTION_SOURCE_BYTES / 1_000_000
          ),
          sizeMb: formatLiveActionNumber(file.size / 1_000_000, 1),
        })
      );
      return;
    }
    if (!resetLiveActionSource()) return;
    setLiveActionFile(file);
    setLiveActionPreviewUrl(URL.createObjectURL(file));
  };

  const handleLiveActionAssetSelect = (asset: UploadedAsset) => {
    if (!resetLiveActionSource()) return;
    setLiveActionLocation(asset.location);
    setLiveActionPreviewUrl(getAssetDownloadUrl(asset.location, videoSasToken));
  };

  const handleLiveActionDurationLoaded = (
    event: React.SyntheticEvent<HTMLVideoElement>
  ) => {
    const duration = event.currentTarget.duration;
    if (!Number.isFinite(duration) || duration <= 0) return;
    setLiveActionDuration(duration);
    if (duration > LIVE_ACTION_MAX_SECONDS) {
      setLiveActionError(
        liveActionMessage('sourceTooLongActual', {
          maxSeconds: formatLiveActionNumber(LIVE_ACTION_MAX_SECONDS),
          actualSeconds: formatLiveActionNumber(Math.ceil(duration)),
        })
      );
    } else if (duration < LIVE_ACTION_MIN_SECONDS) {
      setLiveActionError(
        liveActionMessage('sourceTooShortActual', {
          minSeconds: formatLiveActionNumber(LIVE_ACTION_MIN_SECONDS),
          actualSeconds: formatLiveActionNumber(Math.floor(duration)),
        })
      );
    } else {
      setLiveActionError('');
    }
  };

  const handleEnvironmentSelect = (environmentId: string) => {
    if (!confirmDiscardGeneratedVideo()) return;
    if (selectedEnvironmentId === environmentId) {
      setSelectedEnvironmentId(null);
      clearGeneratedOutputs();
      return;
    }
    const environment = environments.find((entry) => entry.id === environmentId);
    if (
      environment &&
      !environment.referenceImages.every((image) =>
        isImageAspectRatioAllowed(image.width, image.height)
      )
    ) {
      return;
    }
    setSelectedEnvironmentId(environmentId);
    clearGeneratedOutputs();
  };

  const selectedEnvironment =
    environments.find((env) => env.id === selectedEnvironmentId) ?? null;
  const selectedEnvironmentThumbnail = useCharacterImageUrl(
    selectedEnvironment?.referenceImages[0]?.url
  );

  const handleLibraryCharacterSelect = async (characterId: string) => {
    let currentIds = selectedLibraryCharacterIdsRef.current;

    // Toggle off an already-selected character.
    if (currentIds.includes(characterId)) {
      if (!confirmDiscardGeneratedVideo()) return;
      const nextIds = currentIds.filter((id) => id !== characterId);
      selectedLibraryCharacterIdsRef.current = nextIds;
      setSelectedLibraryCharacterIds(nextIds);
      setCharacterImageMap((prev) => {
        const next = { ...prev };
        delete next[characterId];
        return next;
      });
      setSelectedVoiceReferences((prev) => {
        const next = { ...prev };
        delete next[characterId];
        return next;
      });
      clearGeneratedOutputs();
      return;
    }

    const selectedCharacter = libraryCharacters.find(
      (character) => character.id === characterId
    );

    if (!selectedCharacter) {
      return;
    }
    if (
      !isImageAspectRatioAllowed(
        selectedCharacter.imageWidth,
        selectedCharacter.imageHeight
      )
    ) {
      return;
    }

    const replacesLiveActionCharacter = isLiveAction && currentIds.length > 0;
    if (
      !replacesLiveActionCharacter &&
      currentIds.length >= maxSelectedCharacters
    ) {
      toast.error(
        toastMessage(
          maxSelectedCharacters === 1
            ? 'characterVideo.maxCharactersOne'
            : 'characterVideo.maxCharactersMany',
          { count: maxSelectedCharacters }
        )
      );
      return;
    }
    if (!confirmDiscardGeneratedVideo()) return;

    // Live-action takes a single reference character: a new pick replaces
    // the previous one instead of being rejected.
    if (replacesLiveActionCharacter) {
      currentIds = [];
      selectedLibraryCharacterIdsRef.current = [];
      setSelectedLibraryCharacterIds([]);
      setCharacterImageMap({});
      setSelectedVoiceReferences({});
    }

    // Library picks clear any custom upload (no mixing).
    const nextIds = [...currentIds, characterId];
    selectedLibraryCharacterIdsRef.current = nextIds;
    setSelectedLibraryCharacterIds(nextIds);
    setCustomImagePreview('');

    // The stored imageUrl may be a relative blob path in the user's storage
    // directory; resolve it with the user SAS token before previewing and
    // sending it to the video generator.
    const resolvedImage = await resolveCharacterImageUrl(
      selectedCharacter.imageUrl
    );
    if (resolvedImage) {
      setCharacterImageMap((prev) => ({
        ...prev,
        [characterId]: resolvedImage,
      }));
    } else if (isLiveAction) {
      toast.error(toastMessage('characterVideo.characterNoImage'));
      selectedLibraryCharacterIdsRef.current = currentIds;
      setSelectedLibraryCharacterIds(currentIds);
      return;
    }

    clearGeneratedOutputs();

    // If the character carries a voice reference clip, resolve it to a signed
    // URL as well so it can be forwarded inside `characters[].voiceUrl`.
    const voiceLocation = selectedCharacter.voiceUrl;
    if (voiceLocation) {
      try {
        const resolvedVoiceUrl = await resolveCharacterVoiceUrl(voiceLocation);
        if (resolvedVoiceUrl) {
          const voiceAsset = selectedCharacter.assets?.find(
            (asset) => asset.type === 'audio_reference'
          );
          setSelectedVoiceReferences((prev) => ({
            ...prev,
            [characterId]: {
              url: resolvedVoiceUrl,
              label: voiceAsset?.name || 'voice reference',
            },
          }));
        }
      } catch {
        // No voice for this character; entry simply stays absent.
      }
    }

    setGeneratedVideoUrl('');
    setGeneratedVideoPrompt('');
    setGeneratedVideoDuration(undefined);
    setGeneratedScenes([]);
    setSceneDurationOverride(null);
    setClipDurationBounds(null);
  };

  const handleImageUpload = async (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const input = event.target;
    const file = input.files?.[0];
    if (!file) return;

    const SUPPORTED_TYPES = ['image/jpeg', 'image/png', 'image/webp'];
    const SUPPORTED_EXTENSIONS = ['.jpg', '.jpeg', '.png', '.webp'];
    const normalizedType = file.type.toLowerCase();
    const normalizedName = file.name.toLowerCase();
    const isSupported =
      SUPPORTED_TYPES.includes(normalizedType) ||
      SUPPORTED_EXTENSIONS.some((ext) => normalizedName.endsWith(ext));

    if (!isSupported) {
      toast.error(toastMessage('common.imageTypeUnsupported'));
      input.value = '';
      return;
    }
    if (!confirmDiscardGeneratedVideo()) {
      input.value = '';
      return;
    }

    try {
      const { width, height, dataUrl } = await readImageFile(file);
      if (!isImageAspectRatioAllowed(width, height)) {
        return;
      }
      // Custom uploads clear the whole library selection (no mixing).
      selectedLibraryCharacterIdsRef.current = [];
      setSelectedLibraryCharacterIds([]);
      setCharacterImageMap({});
      setSelectedVoiceReferences({});
      setCustomImagePreview(dataUrl);
      setPreviewIndex(0);
      setGeneratedVideoUrl('');
      setGeneratedVideoPrompt('');
      setGeneratedVideoDuration(undefined);
      setGeneratedScenes([]);
      setSceneDurationOverride(null);
      setClipDurationBounds(null);
    } catch {
      toast.error(toastMessage('common.imageDimensionsUnreadable'));
    } finally {
      input.value = '';
    }
  };

  // Display name helper for a library character.
  const getCharacterDisplayName = (
    character: CharacterRig
  ): string | undefined =>
    character.name ||
    character.identity?.name ||
    character.imageName ||
    undefined;

  // Character references for the video call (name + image + voice URL; the
  // backend constructs the model image/audio references itself). One entry per selected character; entries without a
  // voice are still included by name so backend "Character N" fallbacks stay
  // index-aligned with the script stage.
  const buildVideoCharacters = (): ScriptCharacterReference[] => {
    const entries: ScriptCharacterReference[] = [];
    for (const id of selectedLibraryCharacterIds) {
      const selectedCharacter = libraryCharacters.find(
        (character) => character.id === id
      );
      if (!selectedCharacter) continue;
      const name = getCharacterDisplayName(selectedCharacter);
      const voiceUrl = selectedVoiceReferences[id]?.url;
      const description =
        selectedCharacter.description ||
        selectedCharacter.identity?.description;
      const built: ScriptCharacterReference = {
        ...(name ? { name } : {}),
        // Live-action replacement needs the character description in the
        // conversion prompt; normal video generation already derives it from
        // the script stage.
        ...(isLiveAction && description ? { description } : {}),
        // Resolved stored image for this character; the backend feeds it to
        // the video model as an image reference (signed URL, never a dataURL
        // here — custom uploads travel as the top-level image instead).
        ...(characterImageMap[id] ? { image: characterImageMap[id] } : {}),
        ...(voiceUrl ? { voiceUrl } : {}),
      };
      if (Object.keys(built).length > 0) entries.push(built);
    }
    return entries;
  };

  // Full character reference for the script planner: identity metadata plus
  // the stored (resolved) image and voice of one library character.
  const buildScriptCharacterReference = (
    selectedCharacter: CharacterRig
  ): ScriptCharacterReference => {
    const identity = selectedCharacter.identity;
    const mergedTags = [
      ...(selectedCharacter.tags ?? []),
      ...(identity?.tags ?? []),
    ];
    const built: ScriptCharacterReference = {
      ...(selectedCharacter.id ? { id: selectedCharacter.id } : {}),
      name: getCharacterDisplayName(selectedCharacter),
      description:
        selectedCharacter.description || identity?.description || undefined,
      species: identity?.species || undefined,
      gender: identity?.gender || undefined,
      personality:
        identity?.personality && identity.personality.length > 0
          ? identity.personality
          : undefined,
      biography: identity?.biography || undefined,
      tags: mergedTags.length > 0 ? mergedTags : undefined,
      imageName: selectedCharacter.imageName || undefined,
      // Resolved stored image for this character; sent as-is.
      image: characterImageMap[selectedCharacter.id] || undefined,
      voiceUrl: selectedVoiceReferences[selectedCharacter.id]?.url || undefined,
    };
    // Strip empty-string values so the backend receives a clean payload.
    return Object.fromEntries(
      Object.entries(built).filter(
        ([, value]) => value !== undefined && value !== ''
      )
    ) as ScriptCharacterReference;
  };

  // Environment snapshot for generation calls (by value, like
  // characters[]): id/name/description plus resolved (signed) reference
  // image URLs with their user labels. Returns undefined when no
  // environment is selected or none of its images resolve.
  const buildEnvironmentSnapshot = async (): Promise<
    EnvironmentSnapshot | undefined
  > => {
    if (!selectedEnvironment) return undefined;
    const referenceImages: { url: string; name?: string }[] = [];
    for (const img of selectedEnvironment.referenceImages) {
      const resolved = await resolveCharacterImageUrl(img.url);
      if (!resolved) continue;
      referenceImages.push({
        url: resolved,
        ...(img.name?.trim() ? { name: img.name.trim() } : {}),
      });
    }
    if (referenceImages.length === 0) return undefined;
    return {
      ...(selectedEnvironment.id ? { id: selectedEnvironment.id } : {}),
      ...(selectedEnvironment.name?.trim()
        ? { name: selectedEnvironment.name.trim() }
        : {}),
      ...(selectedEnvironment.description?.trim()
        ? { description: selectedEnvironment.description.trim() }
        : {}),
      referenceImages,
    };
  };

  const hasCharacterSource =
    customImagePreview !== '' || selectedLibraryCharacterIds.length > 0;

  const handleGenerateScript = async () => {
    if (!hasCharacterSource) {
      toast.error(toastMessage('characterVideo.characterImageRequired'));
      return;
    }

    if (!prompt.trim()) {
      toast.error(toastMessage('characterVideo.describeCharacter'));
      return;
    }

    const token = localStorage.getItem('authToken');
    if (!token) {
      toast.error(toastMessage('common.loginRequired'));
      return;
    }
    if (!confirmDiscardGeneratedVideo()) return;

    setIsGeneratingScript(true);
    try {
      const trimmedPrompt = composeAnimationPrompt(
        activeCharacterStylePreset,
        prompt.trim()
      );
      // Build one complete character reference per selected library
      // character. Custom uploads (no library selection) send just the image.
      const characters: ScriptCharacterReference[] = [];
      for (const id of selectedLibraryCharacterIds) {
        const selectedCharacter = libraryCharacters.find(
          (character) => character.id === id
        );
        if (!selectedCharacter) continue;
        const cleaned = buildScriptCharacterReference(selectedCharacter);
        if (Object.keys(cleaned).length > 0) characters.push(cleaned);
      }

      // Environment snapshot (by value, like characters[]): resolved
      // reference image URLs + labels for the selected environment.
      const environmentSnapshot = await buildEnvironmentSnapshot();

      const result = await generateVideoScript(trimmedPrompt, token, {
        // Custom-upload path: no library characters, send the image alone.
        // Library path: primary image stays empty; the backend picks images
        // from `characters[].image`.
        ...(characters.length
          ? {}
          : customImagePreview
            ? { image: customImagePreview }
            : {}),
        ...(characters.length ? { characters } : {}),
        ...(environmentSnapshot ? { environment: environmentSnapshot } : {}),
        // Same model the video stage will use, so per-scene durations are
        // capped to what that model can generate in one shot.
        model: isRunwayGen4TurboEnabled() ? 'gen4_turbo' : 'seedance2_5',
      });
      setGeneratedScenes(result.scenes);
      setSceneDurationOverride(null);
      setClipDurationBounds(result.clipDuration ?? null);
      // Server-published resolution capabilities (fallbacks keep 720p-only
      // when an older backend omits them).
      setResolutionOptions(
        result.resolutions?.length ? result.resolutions : ['720p']
      );
      setResolution(result.defaultResolution ?? '720p');
      if (result.resolutionPixelsPerSecond) {
        setPixelsPerSecondMap(result.resolutionPixelsPerSecond);
      }
      setGeneratedVideoUrl('');
      setGeneratedVideoPrompt('');
      setGeneratedVideoDuration(undefined);
      setDraftTaskId(null);
      setDraftDurationSeconds(null);
      // A new script means a new estimate; drop any stale 402/403 banners.
      setInsufficientCredits(null);
      setNoActiveEntitlement(false);
      toast.success(toastMessage('characterVideo.scriptGenerated'));
    } catch (error: any) {
      if (error instanceof NoActiveEntitlementError) {
        setNoActiveEntitlement(true);
        toast.error(
          error.message || toastMessage('common.subscriptionRequired')
        );
      } else if (error instanceof InsufficientPixlesError) {
        // The 402 body may omit balances for cheap calls — only render the
        // banner (and sync the pill) when the backend reported numbers.
        if (error.required > 0) {
          setInsufficientCredits({
            required: error.required,
            available: error.available,
          });
          setPixelsBalance(error.available);
        }
        toast.error(
          error.required > 0
            ? toastMessage('characterVideo.scriptPixels', {
                required: error.required,
                available: error.available,
              })
            : error.message || toastMessage('common.insufficientPixels')
        );
      } else {
        toast.error(error?.message || toastMessage('characterVideo.scriptFailed'));
      }
    } finally {
      setIsGeneratingScript(false);
    }
  };

  const handleGeneratedSceneChange = (sceneNumber: number, value: string) => {
    setGeneratedScenes((prev) =>
      prev.map((entry) =>
        entry.scene === sceneNumber ? { ...entry, prompt: value } : entry
      )
    );
    // Any edit invalidates a video previously generated from the old script.
    setGeneratedVideoUrl('');
    setGeneratedVideoPrompt('');
    setGeneratedVideoDuration(undefined);
    setDraftTaskId(null);
    setDraftDurationSeconds(null);
  };

  // Single-scene flow: the planner may return up to 2 scenes, but we shoot
  // Scene 1 only. Everything below (guard, video prompt, editor, badges)
  // reads the first scene; the rest are ignored until multi-stitch exists.
  const firstScene = generatedScenes[0];
  const scene1Prompt = firstScene?.prompt.trim() ?? '';
  const scene1EstimatedSeconds = firstScene?.estimatedDurationSeconds ?? 0;
  const liveActionSourceSelected = Boolean(
    liveActionFile || liveActionLocation
  );
  const liveActionTooLong =
    liveActionDuration !== null && liveActionDuration > LIVE_ACTION_MAX_SECONDS;
  const liveActionTooShort =
    liveActionDuration !== null && liveActionDuration < LIVE_ACTION_MIN_SECONDS;
  const liveActionCharacterId = selectedLibraryCharacterIds[0];
  const liveActionCharacterReady =
    !liveActionCharacterId || Boolean(characterImageMap[liveActionCharacterId]);
  const activeStylePreset =
    ANIMATION_STYLE_PRESETS.find(
      (preset) => preset.id === liveActionStyleId
    ) ?? null;
  const activeCharacterStylePreset =
    ANIMATION_STYLE_PRESETS.find(
      (preset) => preset.id === characterStyleId
    ) ?? null;
  const liveActionPrompt = composeAnimationPrompt(activeStylePreset, prompt);
  const liveActionExtraLimit = Math.max(
    0,
    MAX_LIVE_ACTION_PROMPT_CHARS -
      (activeStylePreset ? activeStylePreset.fragment.length + 2 : 0)
  );
  const canGenerateLiveAction =
    Boolean(videoId) &&
    liveActionSourceSelected &&
    liveActionDuration !== null &&
    !liveActionTooLong &&
    !liveActionTooShort &&
    liveActionCharacterReady &&
    liveActionPrompt.length > 0 &&
    liveActionPrompt.length <= MAX_LIVE_ACTION_PROMPT_CHARS;
  // Kling edits in place: output length follows the source clip.
  const effectiveLiveActionDuration =
    liveActionDuration !== null ? Math.round(liveActionDuration) : undefined;
  const effectiveDurationSeconds = isLiveAction
    ? effectiveLiveActionDuration
    : (sceneDurationOverride ??
      (scene1EstimatedSeconds > 0 ? scene1EstimatedSeconds : undefined));
  // Per-second rate for the selected tier. Live action uses Kling's
  // input-video rates instead of the text-to-video rate map.
  const activePixelsPerSecond = isLiveAction
    ? (LIVE_ACTION_RATES[resolution] ?? LIVE_ACTION_RATES['720p'])
    : (pixelsPerSecondMap[resolution] ??
      VIDEO_PIXELS_PER_SECOND[resolution] ??
      30);
  // Pre-flight credit estimate at the selected resolution. Kling bills output
  // seconds plus the longer of source and output seconds.
  const scene1EstimateCredits = isLiveAction
    ? estimateLiveActionPixels({
        outputSeconds: liveActionDuration ?? 0,
        inputSeconds: liveActionDuration ?? 0,
        outputPixelsPerSecond: activePixelsPerSecond,
      })
    : effectiveDurationSeconds !== undefined
      ? Math.max(1, Math.round(effectiveDurationSeconds)) *
        activePixelsPerSecond
      : 0;
  // Gate the generate button when a known balance can't cover the estimate.
  const isBalanceInsufficient =
    pixelsBalance !== null &&
    scene1EstimateCredits > 0 &&
    pixelsBalance < scene1EstimateCredits;
  // Enhance is billed at 1080p; live-action also re-bills the source video.
  const enhanceEstimateCredits =
    draftDurationSeconds !== null && draftDurationSeconds > 0
      ? isLiveAction
        ? estimateLiveActionPixels({
            outputSeconds: draftDurationSeconds,
            inputSeconds: liveActionDuration ?? 0,
            outputPixelsPerSecond:
              pixelsPerSecondMap['1080p'] ?? VIDEO_PIXELS_PER_SECOND['1080p'],
          })
        : Math.max(1, Math.round(draftDurationSeconds)) *
          (pixelsPerSecondMap['1080p'] ?? VIDEO_PIXELS_PER_SECOND['1080p'])
      : 0;
  const canEnhance = draftTaskId !== null;
  // Video-stage work (generation or enhancement) vs. any in-flight work.
  const isVideoBusy = isGenerating || isEnhancing;
  const isBusy =
    isVideoBusy ||
    isGeneratingScript ||
    isSavingToTimeline ||
    isUploadingSource;

  const applyGeneratedResult = (
    result: GenerateCharacterVideoResponse,
    promptText: string,
    fallbackDuration?: number
  ) => {
    setGeneratedVideoUrl(result.videoUrl);
    setGeneratedVideoPrompt(promptText);
    setGeneratedVideoDuration(result.duration);
    // A 480p result is an enhanceable draft: keep its handle + duration.
    setDraftTaskId(result.canEnhance ? (result.draftTaskId ?? null) : null);
    setDraftDurationSeconds(
      result.canEnhance
        ? (result.durationSeconds ?? fallbackDuration ?? null)
        : null
    );
    // Update the balance display immediately from the completion payload;
    // fall back to the existing (stale) balance when the backend omits it.
    if (typeof result.available === 'number') {
      setPixelsBalance(result.available);
    }
  };

  const handleGenerateVideo = async () => {
    if (isLiveAction) {
      if (!videoId) {
        toast.error(liveActionMessage('openProject'));
        return;
      }
      if (!liveActionSourceSelected) {
        toast.error(liveActionMessage('sourceRequired'));
        return;
      }
      if (liveActionTooLong) {
        toast.error(
          liveActionMessage('sourceTooLong', {
            maxSeconds: formatLiveActionNumber(LIVE_ACTION_MAX_SECONDS),
          })
        );
        return;
      }
      if (liveActionTooShort) {
        toast.error(
          liveActionMessage('sourceTooShort', {
            minSeconds: formatLiveActionNumber(LIVE_ACTION_MIN_SECONDS),
          })
        );
        return;
      }
      if (liveActionDuration === null) {
        toast.error(liveActionMessage('waitForDuration'));
        return;
      }
      if (!liveActionPrompt) {
        toast.error(liveActionMessage('promptRequired'));
        return;
      }
      if (liveActionPrompt.length > MAX_LIVE_ACTION_PROMPT_CHARS) {
        toast.error(
          liveActionMessage('promptTooLong', {
            maxChars: formatLiveActionNumber(MAX_LIVE_ACTION_PROMPT_CHARS),
          })
        );
        return;
      }
    } else {
      if (!hasCharacterSource) {
        toast.error(toastMessage('characterVideo.characterImageRequired'));
        return;
      }

      if (!scene1Prompt) {
        toast.error(toastMessage('characterVideo.scriptRequired'));
        return;
      }
    }

    const token = localStorage.getItem('authToken');
    if (!token) {
      toast.error(toastMessage('common.loginRequired'));
      return;
    }
    if (!confirmDiscardGeneratedVideo()) return;

    setIsGenerating(true);
    setVideoProgress(0);
    setVideoProgressSource('estimated');
    // Fresh attempt: clear any prior 402/403 banners. No auto-retry is
    // performed on insufficient credits — the user must top up and retry
    // manually.
    setInsufficientCredits(null);
    setNoActiveEntitlement(false);

    const onProgress = (event: {
      progress?: number;
      progressSource?: 'provider' | 'estimated';
    }) => {
      if (typeof event?.progress === 'number') {
        setVideoProgress(event.progress);
      }
      if (
        event?.progressSource === 'provider' ||
        event?.progressSource === 'estimated'
      ) {
        setVideoProgressSource(event.progressSource);
      }
    };

    try {
      if (isLiveAction) {
        let sourceVideo = liveActionLocation;
        if (!sourceVideo && liveActionFile) {
          setIsUploadingSource(true);
          try {
            sourceVideo = await uploadAssetToVideo(
              liveActionFile,
              videoId as string,
              token
            );
            setLiveActionLocation(sourceVideo);
            const asset: UploadedAsset = {
              location: sourceVideo,
              type: 'video',
              videoId,
            };
            setLiveActionAssets((prev) => [
              asset,
              ...prev.filter((entry) => entry.location !== sourceVideo),
            ]);
            try {
              await saveUploadedAsset(
                { location: sourceVideo, type: 'video', videoId },
                token
              );
            } catch (saveError) {
              console.error(
                'Failed to persist live-action source asset:',
                saveError
              );
            }
          } finally {
            setIsUploadingSource(false);
          }
        }
        if (!sourceVideo) {
          throw new Error(liveActionMessage('sourceRequired'));
        }
        const result = await handleLiveActionVideoAnimation(
          {
            videoId: videoId as string,
            sourceVideo,
            prompt: liveActionPrompt,
            resolution,
            audioMode: liveActionAudioMode,
            characters: buildVideoCharacters().slice(0, 1),
          },
          token,
          onProgress
        );
        applyGeneratedResult(
          result,
          liveActionPrompt,
          effectiveLiveActionDuration
        );
      } else {
        // Primary image is the attached upload only (may be empty for
        // library-only flows); the backend picks library images from
        // `characters[].image` to feed the video model.
        // The full scenes array travels (prompts + durations together); the
        // backend shoots Scene 1 today. The user's length override is folded
        // into Scene 1's estimate so there is a single duration source.
        const scenesToSend = generatedScenes.map((entry, index) => ({
          ...entry,
          prompt: entry.prompt.trim(),
          ...(index === 0 && effectiveDurationSeconds !== undefined
            ? { estimatedDurationSeconds: effectiveDurationSeconds }
            : {}),
        }));
        // Environment snapshot (by value, like characters[]): resolved
        // reference image URLs + labels for the selected environment.
        const environmentSnapshot = await buildEnvironmentSnapshot();
        const result = await handleCharacterVideoGeneration(
          scenesToSend,
          customImagePreview,
          token,
          buildVideoCharacters(),
          onProgress,
          {
            generateAudio: includeAudio,
            ...(environmentSnapshot
              ? { environment: environmentSnapshot }
              : {}),
            resolution,
          }
        );
        applyGeneratedResult(result, scene1Prompt, effectiveDurationSeconds);
      }
      toast.success(
        toastMessage(
          isLiveAction
            ? 'characterVideo.animationReady'
            : 'characterVideo.videoReady'
        )
      );
    } catch (error: any) {
      if (error instanceof NoActiveEntitlementError) {
        setNoActiveEntitlement(true);
        toast.error(
          error.message || toastMessage('common.subscriptionRequired')
        );
      } else if (error instanceof InsufficientPixlesError) {
        setInsufficientCredits({
          required: error.required,
          available: error.available,
        });
        // Keep the pill in sync with the 402-reported balance.
        setPixelsBalance(error.available);
        toast.error(
          toastMessage('characterVideo.videoPixels', {
            required: error.required,
            available: error.available,
          })
        );
      } else {
        toast.error(error?.message || toastMessage('characterVideo.videoFailed'));
      }
    } finally {
      setIsGenerating(false);
      setIsUploadingSource(false);
      setVideoProgress(null);
      setVideoProgressSource(null);
    }
  };

  // Enhance a completed 480p draft preview to 1080p. Reuses the preview's
  // inputs server-side (we send only the draft handle). Uses the same SSE +
  // resume machinery as a normal generation.
  const handleEnhanceVideo = async () => {
    if (!draftTaskId) return;
    const token = localStorage.getItem('authToken');
    if (!token) {
      toast.error(toastMessage('common.loginRequired'));
      return;
    }

    setIsEnhancing(true);
    setVideoProgress(0);
    setVideoProgressSource('estimated');
    setInsufficientCredits(null);
    setNoActiveEntitlement(false);
    try {
      const result = await handleVideoDraftEnhancement(
        draftTaskId,
        token,
        (event) => {
          if (typeof event?.progress === 'number') {
            setVideoProgress(event.progress);
          }
          if (
            event?.progressSource === 'provider' ||
            event?.progressSource === 'estimated'
          ) {
            setVideoProgressSource(event.progressSource);
          }
        }
      );
      setGeneratedVideoUrl(result.videoUrl);
      setResolution('1080p');
      // The draft has been consumed: a preview can only be enhanced once.
      setDraftTaskId(null);
      setDraftDurationSeconds(null);
      if (typeof result.available === 'number') {
        setPixelsBalance(result.available);
      }
      toast.success(toastMessage('characterVideo.enhanced'));
    } catch (error: any) {
      if (error instanceof NoActiveEntitlementError) {
        setNoActiveEntitlement(true);
        toast.error(
          error.message || toastMessage('common.subscriptionRequired')
        );
      } else if (error instanceof InsufficientPixlesError) {
        setInsufficientCredits({
          required: error.required,
          available: error.available,
        });
        setPixelsBalance(error.available);
        toast.error(
          toastMessage('characterVideo.enhancePixels', {
            required: error.required,
            available: error.available,
          })
        );
      } else {
        const message = error?.message || toastMessage('characterVideo.enhanceFailed');
        toast.error(message);
        // A dropped handle means the draft is unusable (expired/foreign) —
        // clear it so the stale button disappears.
        if (/no longer be enhanced|draft not found/i.test(message)) {
          setDraftTaskId(null);
          setDraftDurationSeconds(null);
        }
      }
    } finally {
      setIsEnhancing(false);
      setVideoProgress(null);
      setVideoProgressSource(null);
    }
  };

  // Billing CTAs from the credit banners route to the billing page, where
  // checkout (Stripe-hosted) and subscription management live.
  const handleTopUpClick = () => {
    navigate('/billing');
  };

  const handleSubscribeClick = () => {
    navigate('/billing');
  };

  useEffect(() => {
    setVideoTrimStart(0);
    setVideoTrimEnd(0);
  }, [generatedVideoUrl]);

  const trimSliderMax =
    generatedVideoDuration && generatedVideoDuration > 0
      ? generatedVideoDuration
      : 0;
  const isTrimRangeValid = isTrimRangeValidFn(
    videoTrimStart,
    videoTrimEnd,
    trimSliderMax
  );
  const isFullVideoRange = isFullTrimRange(
    videoTrimStart,
    videoTrimEnd,
    trimSliderMax
  );
  const isSilentPreview = isLiveAction && liveActionAudioMode === 'none';

  useEffect(() => {
    if (trimSliderMax > 0 && videoTrimEnd <= 0) {
      setVideoTrimStart(0);
      setVideoTrimEnd(trimSliderMax);
    }
  }, [trimSliderMax, videoTrimEnd]);

  const handleVideoDurationChange = (duration: number) => {
    setGeneratedVideoDuration(duration);
    setVideoTrimStart(0);
    setVideoTrimEnd(duration);
  };

  const handleAddToTimeline = async () => {
    if (!generatedVideoUrl) {
      toast.error(toastMessage('characterVideo.timelineVideoRequired'));
      return;
    }
    if (!isTrimRangeValid) {
      toast.error(toastMessage('characterVideo.trimTooShort'));
      return;
    }

    setIsSavingToTimeline(true);
    try {
      await onVideoGenerated(
        generatedVideoUrl,
        generatedVideoPrompt ||
          (isLiveAction ? liveActionPrompt : prompt.trim()),
        {
          duration: generatedVideoDuration,
          trim:
            isFullVideoRange || trimSliderMax <= 0
              ? undefined
              : {
                  startTime: Number(videoTrimStart.toFixed(3)),
                  endTime: Number(videoTrimEnd.toFixed(3)),
                },
          mute: isSilentPreview,
        }
      );
    } catch {
      // The parent surfaces its own upload errors and returns; this covers a
      // later failure (e.g. the timeline PATCH) so it is never silent.
      toast.error(toastMessage('characterVideo.saveFailed'));
    } finally {
      setIsSavingToTimeline(false);
    }
  };

  const hasGeneratedScript = generatedScenes.length > 0;
  const hasGeneratedVideo = Boolean(generatedVideoUrl);

  // Keep the parent's close guard in sync: a generated video is unsaved
  // until it is handed off via Save & Add to Timeline (which unmounts us).
  useEffect(() => {
    onUnsavedChange?.(hasGeneratedVideo);
  }, [hasGeneratedVideo, onUnsavedChange]);

  const requestCancel = () => {
    onCancel();
  };

  // The gen4_turbo feature-flag model has no audio-reference support; only
  // the seedance models consume the voice clip, so keep the hint honest.
  const audioSupported = !isRunwayGen4TurboEnabled();
  // Per-character voice notes, in selection order.
  const voiceNotes: { id: string; name: string; text: string }[] = [];
  for (const id of selectedLibraryCharacterIds) {
    const voice = selectedVoiceReferences[id];
    if (!voice) continue;
    const character = libraryCharacters.find((c) => c.id === id);
    const name =
      (character && getCharacterDisplayName(character)) || 'Character';
    voiceNotes.push({
      id,
      name,
      text: audioSupported
        ? `Voice reference "${voice.label}" (${name}) will be sent to the video model as audio input.`
        : `Voice reference "${voice.label}" (${name}) won't be used — the enabled video model doesn't support audio input.`,
    });
  }

  return (
    <div className="character-video-generator">
      {(pixelsBalance !== null ||
        (trialActive === true && trialDaysRemaining !== null)) && (
        <div
          className="character-video-credits-pill"
          title="Your current pixel-credit balance"
        >
          <PixlesCoin size={22} />
          <span>
            {pixelsBalance !== null
              ? `${pixelsBalance.toLocaleString()} pixles`
              : 'Pixles'}
            {trialActive === true && trialDaysRemaining !== null
              ? ` · Trial: ${trialDaysRemaining}d left`
              : ''}
          </span>
        </div>
      )}
      {insufficientCredits && (
        <div className="character-video-credits-banner" role="alert">
          <div className="character-video-credits-banner-copy">
            <strong>Not enough pixles.</strong>
            <span>
              {' '}
              This video needs {insufficientCredits.required} pixels, you have{' '}
              {insufficientCredits.available}.
            </span>
          </div>
          <div className="character-video-credits-banner-actions">
            <button
              type="button"
              className="character-video-credits-topup"
              onClick={handleTopUpClick}
            >
              Top up pixles
            </button>
            <button
              type="button"
              className="character-video-credits-dismiss"
              onClick={() => setInsufficientCredits(null)}
              aria-label="Dismiss insufficient pixels message"
            >
              Dismiss
            </button>
          </div>
        </div>
      )}
      {noActiveEntitlement && (
        <div className="character-video-credits-banner" role="alert">
          <div className="character-video-credits-banner-copy">
            <strong>Subscription required.</strong>
            <span>
              {' '}
              This video needs an active subscription. Subscribe to Pro to
              generate.
            </span>
          </div>
          <div className="character-video-credits-banner-actions">
            <button
              type="button"
              className="character-video-credits-topup"
              onClick={handleSubscribeClick}
            >
              Subscribe to Pro
            </button>
            <button
              type="button"
              className="character-video-credits-dismiss"
              onClick={() => setNoActiveEntitlement(false)}
              aria-label="Dismiss subscription required message"
            >
              Dismiss
            </button>
          </div>
        </div>
      )}
      <div className="character-video-header-copy">
        <p>
          {isLiveAction
            ? 'Upload a live-action clip up to 30 seconds (or pick one already in this video), then describe the 2D animation style. The clip is converted to animation, keeping its motion and timing.'
            : 'Upload a character image and describe the motion you want. Your prompt will first be sent to an AI script planner that converts it into a detailed animated video script. Review the script, then generate the video.'}
        </p>
      </div>

      {isLiveAction && (
        <div className="character-video-live-action">
          {!videoId && (
            <div className="character-video-live-action-error" role="alert">
              Open a video project before animating a clip.
            </div>
          )}
          <div className="character-video-source-panel">
            <div className="character-video-source-copy">
              <label>Live-action source</label>
              <span>
                Upload a clip up to {LIVE_ACTION_MAX_SECONDS} seconds and 32 MB,
                or choose one already uploaded to this video.
              </span>
            </div>
            <label className="upload-button character-video-live-action-upload">
              <input
                type="file"
                accept="video/*"
                onChange={(event) => {
                  handleLiveActionFileSelect(event.target.files?.[0]);
                  event.target.value = '';
                }}
                disabled={isBusy}
              />
              {isUploadingSource ? 'Uploading…' : 'Upload video'}
            </label>
          </div>

          <div className="character-video-live-action-assets">
            <div className="character-video-source-copy">
              <label>Uploaded videos in this project</label>
              <span>
                {isLoadingLiveActionAssets
                  ? 'Loading videos…'
                  : liveActionAssets.length > 0
                    ? 'Click a clip to use it as the source.'
                    : 'No uploaded videos yet. Upload one above.'}
              </span>
            </div>
            {liveActionAssets.length > 0 && (
              <div className="character-video-live-action-asset-grid">
                {liveActionAssets.map((asset) => {
                  const isSelected = liveActionLocation === asset.location;
                  const fileName = asset.location.split('/').pop() || 'video';
                  return (
                    <button
                      key={asset.location}
                      type="button"
                      className={`character-video-live-action-asset${isSelected ? ' is-selected' : ''}`}
                      onClick={() => handleLiveActionAssetSelect(asset)}
                      disabled={isBusy}
                      title={fileName}
                    >
                      <video
                        src={getAssetDownloadUrl(asset.location, videoSasToken)}
                        muted
                        playsInline
                        preload="metadata"
                      />
                      <span>{fileName}</span>
                    </button>
                  );
                })}
              </div>
            )}
          </div>

          {liveActionPreviewUrl && (
            <div className="character-video-live-action-preview">
              <video
                key={liveActionPreviewUrl}
                src={liveActionPreviewUrl}
                controls
                muted
                playsInline
                onLoadedMetadata={handleLiveActionDurationLoaded}
                onError={() => {
                  setLiveActionDuration(null);
                  setLiveActionError(liveActionMessage('sourceUnreadable'));
                }}
              />
              <button
                type="button"
                className="character-video-live-action-remove"
                onClick={resetLiveActionSource}
                disabled={isBusy}
              >
                Remove
              </button>
            </div>
          )}

          {liveActionError && (
            <div className="character-video-live-action-error" role="alert">
              {liveActionError}
            </div>
          )}

          {liveActionSourceSelected &&
            !liveActionError &&
            liveActionDuration !== null && (
              <div className="character-video-live-action-duration">
                <span>Animation length</span>
                <span>
                  {Math.round(liveActionDuration)} seconds · matches source
                </span>
              </div>
            )}

          <div className="character-video-source-panel">
            <div className="character-video-source-copy">
              <label>Reference character</label>
              <span>
                Optional. Replace the main person in the clip with one of your
                characters; motion and timing are preserved. Replacement is
                automatic, so no need to mention it in the extra direction.
                Works best with one visible person.
              </span>
            </div>

            {onOpenCharacterStudio && (
              <button
                type="button"
                className="character-video-studio-link"
                onClick={() => onOpenCharacterStudio(liveActionCharacterId)}
                disabled={isBusy}
                title="Open Character Studio to modify your characters"
              >
                {liveActionCharacterId
                  ? 'Edit Character in Studio'
                  : 'Open Character Studio'}
              </button>
            )}

            {libraryCharacters.length > 0 ? (
              <div className="character-video-library-grid">
                {libraryCharacters.map((character) => {
                  const isSelected = liveActionCharacterId === character.id;
                  return (
                    <CharacterVideoLibraryCard
                      key={character.id}
                      character={character}
                      isSelected={isSelected}
                      disabled={isBusy}
                      onSelect={() =>
                        handleLibraryCharacterSelect(character.id)
                      }
                    />
                  );
                })}
              </div>
            ) : (
              <div className="character-video-library-empty">
                No saved characters yet. Create one in Character Studio to use
                it as a reference.
              </div>
            )}

            {liveActionCharacterId && !liveActionCharacterReady && (
              <div className="character-video-live-action-error" role="status">
                Loading the character reference…
              </div>
            )}
          </div>

          <div className="character-video-prompt-panel">
            <label htmlFor="character-video-live-action-prompt">
              Extra direction (optional)
            </label>
            <div
              className="character-video-live-action-presets"
              role="group"
              aria-label="Style presets"
            >
              {ANIMATION_STYLE_PRESETS.map((preset) => {
                const isActive = activeStylePreset?.id === preset.id;
                return (
                  <button
                    key={preset.id}
                    type="button"
                    className={`character-video-live-action-preset${
                      isActive ? ' is-active' : ''
                    }`}
                    onClick={() => handleStylePresetToggle(preset)}
                    disabled={isBusy}
                    aria-pressed={isActive}
                    title={preset.fragment}
                  >
                    {preset.label}
                  </button>
                );
              })}
            </div>
            <span className="character-video-live-action-presets-hint">
              The selected style is applied automatically. Tap it again to
              remove it.
            </span>
            <textarea
              id="character-video-live-action-prompt"
              className="action-input-textarea character-video-textarea"
              value={prompt}
              onChange={(event) => {
                if (!confirmDiscardGeneratedVideo()) {
                  event.currentTarget.value = prompt;
                  return;
                }
                setPrompt(event.target.value);
                clearGeneratedOutputs();
              }}
              placeholder={
                'Optional: add rain, keep the dog realistic, no background music…'
              }
              maxLength={liveActionExtraLimit}
              disabled={isBusy}
            />
            <span className="character-video-live-action-count">
              {prompt.length}/{liveActionExtraLimit}
            </span>
          </div>
        </div>
      )}

      {!isLiveAction && (
        <>
          <div className="character-video-source-panel">
            <div className="character-video-source-copy">
              <label>Import from character library</label>
              <span>
                Choose up to {MAX_SELECTED_CHARACTERS} characters to reuse their
                current images in this video workflow.
                {selectedLibraryCharacterIds.length > 0 &&
                  ` Selected ${selectedLibraryCharacterIds.length} of ${MAX_SELECTED_CHARACTERS}.`}
              </span>
            </div>

            {onOpenCharacterStudio && (
              <button
                type="button"
                className="character-video-studio-link"
                onClick={() =>
                  onOpenCharacterStudio(
                    selectedLibraryCharacterIds[0] || undefined
                  )
                }
                disabled={isGeneratingScript || isGenerating}
                title="Open Character Studio to modify your characters"
              >
                {selectedLibraryCharacterIds.length > 0
                  ? `Edit Character${selectedLibraryCharacterIds.length > 1 ? 's' : ''} in Studio`
                  : 'Open Character Studio'}
              </button>
            )}

            {libraryCharacters.length > 0 ? (
              <div className="character-video-library-grid">
                {libraryCharacters.map((character) => {
                  const isSelected = selectedLibraryCharacterIds.includes(
                    character.id
                  );
                  const selectionFull =
                    selectedLibraryCharacterIds.length >=
                      MAX_SELECTED_CHARACTERS && !isSelected;
                  return (
                    <CharacterVideoLibraryCard
                      key={character.id}
                      character={character}
                      isSelected={isSelected}
                      disabled={
                        isGeneratingScript || isGenerating || selectionFull
                      }
                      onSelect={() =>
                        handleLibraryCharacterSelect(character.id)
                      }
                    />
                  );
                })}
              </div>
            ) : (
              <div className="character-video-library-empty">
                No saved characters yet. Upload an image below to start.
              </div>
            )}

            {voiceNotes.map((note) => (
              <div
                key={note.id}
                className={`character-video-voice-note${
                  audioSupported ? '' : ' muted'
                }`}
              >
                <span className="character-video-voice-note-icon">🎙️</span>
                <span>{note.text}</span>
              </div>
            ))}
          </div>

          <div className="character-video-environment-panel">
            <div className="character-video-environment-header">
              <div className="character-video-environment-copy">
                <label>
                  <span
                    className="character-video-environment-icon"
                    aria-hidden="true"
                  >
                    🏞️
                  </span>
                  Environment{' '}
                  <span className="character-video-environment-pill">
                    Optional
                  </span>
                </label>
                <span>
                  Set the scene in a reusable environment so the location stays
                  consistent across videos.
                </span>
              </div>

              {onOpenEnvironmentLibrary && (
                <button
                  type="button"
                  className="character-video-studio-link character-video-env-manage-top"
                  onClick={onOpenEnvironmentLibrary}
                  disabled={isGeneratingScript || isGenerating}
                  title="Open the Environment Library to create or edit environments"
                >
                  Manage environments
                </button>
              )}
            </div>

            {environments.length > 0 ? (
              <div className="character-video-environment-grid">
                {environments.map((environment) => (
                  <EnvironmentVideoOptionCard
                    key={environment.id}
                    environment={environment}
                    isSelected={environment.id === selectedEnvironmentId}
                    disabled={isGeneratingScript || isGenerating}
                    onSelect={() => handleEnvironmentSelect(environment.id)}
                  />
                ))}
              </div>
            ) : (
              <div className="character-video-environment-empty">
                No environments yet.{' '}
                {onOpenEnvironmentLibrary ? (
                  <button
                    type="button"
                    className="character-video-studio-link"
                    onClick={onOpenEnvironmentLibrary}
                    disabled={isGeneratingScript || isGenerating}
                    title="Open the Environment Library to create one"
                  >
                    Create one in the Environment Library
                  </button>
                ) : (
                  'Create one in the Environment Library.'
                )}
              </div>
            )}

            {selectedEnvironment && (
              <div className="character-video-environment-selected">
                {selectedEnvironmentThumbnail ? (
                  <img
                    src={selectedEnvironmentThumbnail}
                    alt={selectedEnvironment.name}
                  />
                ) : null}
                <div className="character-video-environment-selected-copy">
                  <strong>{selectedEnvironment.name}</strong>
                  <span>
                    {selectedEnvironment.referenceImages.length} reference image
                    {selectedEnvironment.referenceImages.length === 1
                      ? ''
                      : 's'}
                    {selectedEnvironment.description
                      ? ` · ${selectedEnvironment.description}`
                      : ''}
                  </span>
                </div>
                <button
                  type="button"
                  className="character-video-environment-clear"
                  onClick={() => {
                    if (!confirmDiscardGeneratedVideo()) return;
                    setSelectedEnvironmentId(null);
                    clearGeneratedOutputs();
                  }}
                  disabled={isGeneratingScript || isGenerating}
                  title="Remove the selected environment"
                >
                  Clear
                </button>
              </div>
            )}
          </div>

          <div className="character-video-workspace">
            <div className="character-video-preview-column">
              <div className="upload-section character-video-upload-section">
                <label className="upload-button">
                  <input
                    type="file"
                    accept="image/jpeg,image/png,image/webp,.jpg,.jpeg,.png,.webp"
                    onChange={handleImageUpload}
                    disabled={isGeneratingScript || isGenerating}
                    style={{ display: 'none' }}
                  />
                  <span>
                    {hasCharacterSource
                      ? 'Change Character Image'
                      : 'Upload Character Image'}
                  </span>
                </label>
              </div>

              {customImagePreview ? (
              <div className="character-video-preview-wrap">
                <div className="action-character-preview character-video-preview">
                  <img src={customImagePreview} alt="Uploaded character" />
                </div>
                {selectedEnvironment && (
                  <CharacterVideoPreviewEnvPip
                    environment={selectedEnvironment}
                  />
                )}
              </div>
            ) : selectedLibraryCharacterIds.length > 0 ? (
              (() => {
                const total = selectedLibraryCharacterIds.length;
                // Clamp when the selection shrinks (e.g. a deselect or a studio
                // delete) so the index never points past the last slide.
                const safeIndex = Math.min(previewIndex, total - 1);
                const activeId = selectedLibraryCharacterIds[safeIndex];
                const activeCharacter = libraryCharacters.find(
                  (c) => c.id === activeId
                );
                const activeName =
                  (activeCharacter &&
                    getCharacterDisplayName(activeCharacter)) ||
                  'Selected character';
                const activeSrc = characterImageMap[activeId];
                const goTo = (index: number) =>
                  setPreviewIndex(((index % total) + total) % total);
                return (
                  <div className="character-video-carousel">
                    <div className="action-character-preview character-video-preview character-video-carousel-slide">
                      {activeSrc ? (
                        <img
                          key={activeId}
                          src={activeSrc}
                          alt={activeName}
                          title={activeName}
                        />
                      ) : (
                        <div className="character-video-empty-preview">
                          Loading…
                        </div>
                      )}
                      <button
                        type="button"
                        className="character-video-carousel-remove"
                        onClick={() => {
                          void handleLibraryCharacterSelect(activeId);
                          setPreviewIndex((prev) => Math.max(0, prev - 1));
                        }}
                        disabled={isGeneratingScript || isGenerating}
                        title={`Remove ${activeName} from selection`}
                        aria-label={`Remove ${activeName} from selection`}
                      >
                        ×
                      </button>
                      <span className="character-video-carousel-caption">
                        {activeName}
                      </span>
                    </div>
                    {selectedEnvironment && (
                      <CharacterVideoPreviewEnvPip
                        environment={selectedEnvironment}
                      />
                    )}
                    {total > 1 && (
                      <div className="character-video-carousel-controls">
                        <button
                          type="button"
                          className="character-video-carousel-nav"
                          onClick={() => goTo(safeIndex - 1)}
                          disabled={isGeneratingScript || isGenerating}
                          aria-label="Show previous character"
                        >
                          ‹
                        </button>
                        <div className="character-video-carousel-dots">
                          {selectedLibraryCharacterIds.map((id, index) => (
                            <button
                              key={id}
                              type="button"
                              className={`character-video-carousel-dot${
                                index === safeIndex ? ' active' : ''
                              }`}
                              onClick={() => goTo(index)}
                              disabled={isGeneratingScript || isGenerating}
                              aria-label={`Show character ${index + 1} of ${total}`}
                            />
                          ))}
                        </div>
                        <button
                          type="button"
                          className="character-video-carousel-nav"
                          onClick={() => goTo(safeIndex + 1)}
                          disabled={isGeneratingScript || isGenerating}
                          aria-label="Show next character"
                        >
                          ›
                        </button>
                      </div>
                    )}
                    <span className="character-video-carousel-count">
                      {safeIndex + 1} / {total}
                    </span>
                  </div>
                );
              })()
            ) : selectedEnvironment && selectedEnvironmentThumbnail ? (
              <div className="action-character-preview character-video-preview">
                <img
                  src={selectedEnvironmentThumbnail}
                  alt={selectedEnvironment.name}
                  title={`Location: ${selectedEnvironment.name}`}
                />
                <span className="character-video-carousel-caption">
                  📍 {selectedEnvironment.name}
                </span>
              </div>
              ) : (
                <div className="character-video-empty-preview">
                  No image selected
                </div>
              )}
            </div>

            <div className="character-video-prompt-panel">
              <label
                htmlFor="character-video-prompt"
                className="character-video-prompt-title"
              >
                Describe your video idea
              </label>
              <div className="character-video-prompt-surface">
                <textarea
                  id="character-video-prompt"
                  className="action-input-textarea character-video-textarea"
                  value={prompt}
                  onChange={(e) => {
                    if (!confirmDiscardGeneratedVideo()) {
                      e.currentTarget.value = prompt;
                      return;
                    }
                    setPrompt(e.target.value);
                    clearGeneratedOutputs();
                  }}
                  placeholder={
                    'Describe the scene, character action, and environment. Choose an animation style below or describe one in your prompt.\n\nExample:\nA confident character walks through a futuristic neon city at night. Cinematic lighting, smooth tracking camera, realistic body movement and natural cloth physics.'
                  }
                  disabled={isGeneratingScript || isGenerating}
                />
                <div className="character-video-prompt-footer">
                  <span className="character-video-prompt-footer-label">
                    Style
                  </span>
                  <span className="character-video-prompt-footer-note">
                    Animation style is applied when the script is generated.
                  </span>
                  <Select
                    id="character-video-style"
                    aria-label="Animation style"
                    className="character-video-style-select"
                    classNames={{
                      popup: { root: 'character-video-style-dropdown' },
                    }}
                    value={characterStyleId ?? ''}
                    onChange={handleCharacterStyleChange}
                    options={CHARACTER_STYLE_OPTIONS}
                    disabled={isGeneratingScript || isGenerating}
                    title="The selected style is applied when the script is generated"
                  />
                </div>
              </div>
            </div>
          </div>

          {hasGeneratedScript && firstScene && (
            <div className="character-video-generated-section">
              <div className="character-video-generated-header">
                <h3>AI-Generated Script</h3>
                <span className="script-badge">Script Planner</span>
                {(sceneDurationOverride ?? scene1EstimatedSeconds) > 0 && (
                  <span
                    className="script-badge"
                    title={
                      sceneDurationOverride != null
                        ? 'Your clip-length override (snapped to the model range server-side)'
                        : 'Scene 1 duration estimate from the script planner'
                    }
                  >
                    {sceneDurationOverride != null
                      ? `${sceneDurationOverride}s clip length`
                      : `~${scene1EstimatedSeconds}s suggested length`}
                  </span>
                )}
              </div>
              <div
                key={firstScene.scene}
                className="character-video-scene-block"
              >
                <div className="character-video-generated-header">
                  <h4>
                    Scene {firstScene.scene}
                    {generatedScenes.length > 1
                      ? ` of ${generatedScenes.length}`
                      : ''}
                    {typeof firstScene.estimatedDurationSeconds ===
                      'number' && (
                      <> · ~{firstScene.estimatedDurationSeconds}s</>
                    )}
                  </h4>
                </div>
                <textarea
                  id={`character-video-script-scene-${firstScene.scene}`}
                  className="action-input-textarea character-video-script-textarea"
                  value={firstScene.prompt}
                  onChange={(e) => {
                    if (!confirmDiscardGeneratedVideo()) {
                      e.currentTarget.value = firstScene.prompt;
                      return;
                    }
                    handleGeneratedSceneChange(
                      firstScene.scene,
                      e.target.value
                    );
                  }}
                  disabled={isGeneratingScript || isGenerating}
                  placeholder="Scene prompt will appear here..."
                  aria-label={`Edit scene ${firstScene.scene} before generating the video`}
                />
              </div>
              <div className="character-video-script-meta">
                <span className="character-video-script-edit-hint">
                  You can edit this scene before generating the video
                  {generatedScenes.length > 1 && (
                    <>
                      {' '}
                      — the planner wrote {generatedScenes.length} scenes, but
                      only Scene 1 is generated for now.
                    </>
                  )}
                </span>
                <label
                  className="character-video-duration-control"
                  title={`Clip length sent to the video model (${durationBounds.min}–${durationBounds.max}s for this model; snapped server-side). Clear to use the planner's estimate.`}
                >
                  Clip length (s)
                  <input
                    type="number"
                    min={durationBounds.min}
                    max={durationBounds.max}
                    step={1}
                    value={sceneDurationOverride ?? scene1EstimatedSeconds}
                    onChange={(e) => {
                      const next =
                        e.target.value === '' ? NaN : Number(e.target.value);
                      setSceneDurationOverride(
                        Number.isFinite(next) ? Math.round(next) : null
                      );
                    }}
                    disabled={isGeneratingScript || isGenerating}
                    aria-label="Override the Scene 1 clip length in seconds"
                  />
                  {sceneDurationOverride != null && (
                    <button
                      type="button"
                      className="character-video-duration-reset"
                      onClick={() => setSceneDurationOverride(null)}
                      disabled={isGeneratingScript || isGenerating}
                      title="Back to the planner's estimate"
                    >
                      Reset
                    </button>
                  )}
                </label>
                <span
                  className="character-video-script-count"
                  title="Total characters in the script"
                >
                  {scene1Prompt.length.toLocaleString()} characters
                </span>
              </div>
            </div>
          )}
        </>
      )}

      <div className="character-video-generated-section">
        <div className="character-video-generated-header">
          <h3>
            {isLiveAction ? 'Animation Preview' : 'Generated Video Preview'}
          </h3>
          {hasGeneratedVideo && generatedVideoPrompt && (
            <span title={generatedVideoPrompt}>
              Prompt: {generatedVideoPrompt.substring(0, 60)}
              {generatedVideoPrompt.length > 60 ? '...' : ''}
            </span>
          )}
        </div>

        {isVideoBusy ? (
          <div className="character-video-empty-video character-video-generating">
            <ClipLoader size={48} color="#36CEDC" />
            <span>
              {isEnhancing ? 'Enhancing to 1080p' : 'Generating your video'}
              {typeof videoProgress === 'number'
                ? `… ${videoProgressSource === 'estimated' ? '~' : ''}${videoProgress}%`
                : '…'}
            </span>
          </div>
        ) : hasGeneratedVideo ? (
          <>
            <GeneratedVideoPlayer
              src={generatedVideoUrl}
              duration={trimSliderMax}
              trimStart={videoTrimStart}
              trimEnd={videoTrimEnd}
              isSilent={isSilentPreview}
              disabled={isSavingToTimeline}
              onDurationChange={handleVideoDurationChange}
              onTrimChange={(start, end) => {
                setVideoTrimStart(start);
                setVideoTrimEnd(end);
              }}
            />
            {canEnhance && (
              <div className="character-video-enhance-row">
                <span className="character-video-enhance-hint">
                  This is a 480p draft preview.
                  {enhanceEstimateCredits > 0
                    ? ` Enhance to 1080p for ~${enhanceEstimateCredits.toLocaleString()} pixles.`
                    : ' Enhance it to 1080p for the final cut.'}
                </span>
                <button
                  className="character-video-enhance-btn"
                  onClick={handleEnhanceVideo}
                  disabled={isBusy}
                  title="Re-render this preview at 1080p (uses the same prompt, characters, environment, and audio)"
                >
                  Enhance to 1080p
                </button>
              </div>
            )}
          </>
        ) : (
          <div className="character-video-empty-video">
            {isLiveAction
              ? 'Click "Animate Video" to convert your clip into 2D animation.'
              : hasGeneratedScript
                ? 'Click "Generate Video" to create the video from the script above.'
                : 'Your generated video preview will appear here.'}
          </div>
        )}
      </div>

      {(hasGeneratedScript || isLiveAction) &&
        (isLiveAction ||
          scene1EstimateCredits > 0 ||
          !isRunwayGen4TurboEnabled()) && (
          <div className="character-video-footer-row">
            {scene1EstimateCredits > 0 && (
              <div className="character-video-credits-estimate">
                <span>
                  Estimated cost: ~{scene1EstimateCredits.toLocaleString()}{' '}
                  pixles for {isLiveAction ? 'this animation' : 'Scene 1'}
                  {pixelsBalance !== null
                    ? ` · Balance: ${pixelsBalance.toLocaleString()}`
                    : ''}
                </span>
                {isBalanceInsufficient && (
                  <span className="character-video-credits-insufficient">
                    This video needs {scene1EstimateCredits.toLocaleString()}{' '}
                    pixels, you have {pixelsBalance?.toLocaleString() ?? 0}. Top
                    up to generate.
                  </span>
                )}
              </div>
            )}
            {isLiveAction && (
              <div
                className="character-video-audio-segmented"
                role="group"
                aria-label="Audio"
              >
                <span className="character-video-audio-label">Audio</span>
                <div className="character-video-audio-options">
                  {(
                    [
                      {
                        value: 'generate',
                        label: 'AI audio',
                        title: 'Use the soundtrack generated with the animation',
                      },
                      {
                        value: 'preserve',
                        label: 'Original',
                        title: 'Keep the original clip audio',
                      },
                      {
                        value: 'none',
                        label: 'Silent',
                        title: 'Mute the preview and remove audio when saving',
                      },
                    ] as const
                  ).map((option) => (
                    <button
                      key={option.value}
                      type="button"
                      className={`character-video-audio-option${
                        liveActionAudioMode === option.value ? ' active' : ''
                      }`}
                      aria-pressed={liveActionAudioMode === option.value}
                      disabled={isBusy}
                      title={option.title}
                      onClick={() => setLiveActionAudioMode(option.value)}
                    >
                      {option.label}
                    </button>
                  ))}
                </div>
              </div>
            )}
            {resolutionOptions.length > 1 && (
              <label
                className="character-video-resolution-select"
                title={
                  isLiveAction
                    ? 'Kling 4.0 renders at 720p or 1080p'
                    : '480p renders a cheap draft preview you can enhance to 1080p later'
                }
              >
                <span className="character-video-resolution-label">
                  Quality
                </span>
                <select
                  value={resolution}
                  disabled={isBusy}
                  onChange={(event) =>
                    setResolution(event.target.value as VideoResolution)
                  }
                >
                  {resolutionOptions.map((option) => (
                    <option key={option} value={option}>
                      {option === '480p'
                        ? '480p (draft preview)'
                        : `${option} (${
                            isLiveAction
                              ? 'input included'
                              : `${pixelsPerSecondMap[option] ?? VIDEO_PIXELS_PER_SECOND[option] ?? ''} px/s`
                          })`}
                    </option>
                  ))}
                </select>
              </label>
            )}
            {!isLiveAction && !isRunwayGen4TurboEnabled() ? (
              <label
                className="character-video-audio-toggle"
                title="Off = silent video (character voices still apply as input context)"
              >
                <input
                  type="checkbox"
                  checked={includeAudio}
                  disabled={isBusy}
                  onChange={(event) => setIncludeAudio(event.target.checked)}
                />
                <span
                  className="character-video-audio-switch"
                  aria-hidden="true"
                />
                <span className="character-video-audio-label">
                  Generate sound
                </span>
              </label>
            ) : null}
          </div>
        )}

      <div className="action-buttons character-video-actions">
        <button
          className="action-btn cancel"
          onClick={requestCancel}
          disabled={isBusy}
        >
          Cancel
        </button>

        {isLiveAction ? (
          <button
            className="action-btn apply"
            onClick={handleGenerateVideo}
            disabled={isBusy || !canGenerateLiveAction || isBalanceInsufficient}
            title={
              isBalanceInsufficient
                ? `This animation needs ${scene1EstimateCredits} pixles, you have ${pixelsBalance}.`
                : undefined
            }
          >
            {isGenerating
              ? isUploadingSource
                ? 'Uploading source…'
                : 'Animating…'
              : hasGeneratedVideo
                ? 'Regenerate Animation'
                : 'Animate Video'}
          </button>
        ) : (
          <>
            {!hasGeneratedScript ? (
              <button
                className="action-btn apply"
                onClick={handleGenerateScript}
                disabled={isBusy || !prompt.trim() || !hasCharacterSource}
              >
                {isGeneratingScript
                  ? 'Generating Script...'
                  : 'Generate Script'}
              </button>
            ) : (
              <button
                className="action-btn apply"
                onClick={handleGenerateScript}
                disabled={isBusy}
              >
                {isGeneratingScript ? 'Regenerating...' : 'Regenerate Script'}
              </button>
            )}

            {hasGeneratedScript && (
              <button
                className="action-btn apply"
                onClick={handleGenerateVideo}
                disabled={isBusy || isBalanceInsufficient}
                title={
                  isBalanceInsufficient
                    ? `This video needs ${scene1EstimateCredits} pixles, you have ${pixelsBalance}.`
                    : undefined
                }
              >
                {isGenerating
                  ? 'Generating Video...'
                  : hasGeneratedVideo
                    ? 'Regenerate Video'
                    : 'Generate Video'}
              </button>
            )}
          </>
        )}

        <button
          className="action-btn apply"
          onClick={handleAddToTimeline}
          disabled={
            isBusy ||
            isSavingToTimeline ||
            !hasGeneratedVideo ||
            !isTrimRangeValid
          }
          title={
            !isTrimRangeValid
              ? 'Select at least 0.1 seconds to save'
              : undefined
          }
        >
          {isSavingToTimeline ? (
            <>
              <ClipLoader size={16} color="#06232a" />
              Saving…
            </>
          ) : (
            'Save & Add to Timeline'
          )}
        </button>
      </div>
    </div>
  );
};

export default CharacterVideoGenerator;
